import "./App.css";
import "./styles/globalmax-palette.css";
import "./styles/hover-interactions.css";
import "./styles/gm-entrance.css";
import "./styles/landing-theme.css";
import "./styles/premium-fintech-ui.css";
import "./styles/app-logo.css";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import "react-toastify/dist/ReactToastify.css";
/* Design system loads last so its token bridge wins over App.css + bootstrap.
   Order within the DS matters: tokens -> layout -> primitives. */
import "./styles/ds/tokens.css";
import "./styles/ds/layout.css";
import "./styles/ds/primitives.css";
/* Dashboard component styles (StatStrip, RoiCountdown, rail cards, legend)
   are reused across Buy, Profile and other pages, so they load globally
   rather than being scoped to the dashboard route. */
import "./Components/dashboard/dashboard.css";
import { ToastContainer } from "react-toastify";
import { AuthProvider } from "./contexts/AuthContext";
import { useTelegramAuth } from "./Common/useTelegramAuth";
import { lazy, Suspense, useEffect } from "react";
import Loader from "./Components/Loader/Loader";
import { useNavigate, useLocation } from "react-router-dom";

/* PublicLanding was the only eagerly-imported route. That pulled the vendored
   landing bundle (~570KB of CSS, including a global `body { background: #fff }`
   and a second full copy of Bootstrap) into the main chunk, so it applied on
   every dashboard page and painted the body white. Lazy, like every other
   route — the CSS now loads only on "/". */
const PublicLanding = lazy(() => import("./Pages/PublicLanding/PublicLanding"));
const PublicAbout = lazy(() => import("./Pages/PublicLanding/About"));
const PublicPricing = lazy(() => import("./Pages/PublicLanding/Pricing"));
const LandingPage = lazy(() => import("./Pages/LandingPage/LandingPage"));
const SignInPage = lazy(() => import("./Pages/SignIn/SignInPage"));
const SignUpPage = lazy(() => import("./Pages/SignUp/SignUpPage"));
const Register = lazy(() => import("./Pages/Register/Register"));
const ForgetPassword = lazy(() => import("./Pages/ForgetPassword/ForgetPassword"));

function AppContent() {
  const {
    isTelegram,
    isAuthenticated,
    loading: telegramLoading,
    userData,
    error: telegramError
  } = useTelegramAuth();
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isTelegram) {
      console.log('Running in Telegram Mini App');
      console.log('User Data:', userData);
      console.log('Authenticated:', isAuthenticated);
      if (telegramError) {
        console.error('Telegram Auth Error:', telegramError);
      }
    }
  }, [isTelegram, isAuthenticated, userData, telegramError]);

  useEffect(() => {
    if (isTelegram && isAuthenticated && !telegramLoading && location.pathname === '/') {
      navigate('/dashboard', { replace: true });
    }
  }, [isTelegram, isAuthenticated, telegramLoading, location.pathname, navigate]);

  if (isTelegram && telegramLoading) {
    return <Loader />;
  }

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path="/dashboard/*" element={<LandingPage />}></Route>
        <Route path="/signin" element={<SignInPage />}></Route>
        <Route path="/signup" element={<SignUpPage />}></Route>
        <Route path="/register" element={<Register />}></Route>
        <Route path="/forget_password" element={<ForgetPassword />}></Route>
        <Route path="/about" element={<PublicAbout />}></Route>
        <Route path="/pricing" element={<PublicPricing />}></Route>
        {isTelegram && isAuthenticated ? (
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        ) : (
          <Route path="/" element={<PublicLanding />} />
        )}
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={1000}
        hideProgressBar={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </>
  );
}

export default App;
